import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import { pcConsoleDataList } from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { toDateTypeLy } from '@intelligence/utils/common';

/** 合并筛选器值，确保 platform_cascader 等必填项始终有默认值（避免 Cascader Object.keys 报错） */
export const mergeConsoleDataFilterValue = (
  defaultValue: Record<string, any>,
  partialValue?: Record<string, any>,
): Record<string, any> => {
  const value = partialValue || {};
  const merged = { ...defaultValue, ...value };
  const platformKey = filterKeyMap.PLATFORMCASCADER;

  if (!merged[platformKey]?.value) {
    merged[platformKey] = defaultValue[platformKey];
  }

  return merged;
};

export const getConsoleDataPeriodFromFilterValue = (
  filterVal: Record<string, any> = {},
): string => {
  const rangeTime = filterVal?.[filterKeyMap.RANGE_TIME];
  if (!rangeTime) return '';
  return rangeTime?.granularity || toDateTypeLy[rangeTime?.type] || '';
};

export const CONSOLE_DATA_SEGMENT = 'consoleData';

export interface IMultiValueMetricConfig {
  /** i18n key，用于页面列名/图例前缀，如 dbi_metric_alinea_ps_price */
  columnTitlePrefix: string;
  /** 下载/无 i18n 时的英文列名前缀 */
  downloadTitlePrefix: string;
}

/** Alinea Console PS/Xbox Price 多值指标配置 */
export const MULTI_VALUE_METRIC_CONFIG: Record<string, IMultiValueMetricConfig> = {
  alinea__ps_price: {
    columnTitlePrefix: 'dbi_metric_alinea_ps_price',
    downloadTitlePrefix: 'Alinea PS Price',
  },
  alinea__xbox_price: {
    columnTitlePrefix: 'dbi_metric_alinea_xbox_price',
    downloadTitlePrefix: 'Alinea Xbox Price',
  },
};

export const multiValueMetricList = Object.keys(MULTI_VALUE_METRIC_CONFIG);

export const CONSOLE_DATA_DEFAULT_METRIC =
  pcConsoleDataList?.[0]?.value || 'alinea__ps_est_revenue';

export const CONSOLE_DATA_PS_PLAYERS_BY_COUNTRY = 'alinea__ps_players_by_country';

export const CONSOLE_DATA_COUNTRIES_DEFAULT_METRIC =
  CONSOLE_DATA_PS_PLAYERS_BY_COUNTRY;

export const consoleDataPlayersByCountryMetric = {
  text: 'dbi_metric_alinea_ps_players_by_country',
  value: CONSOLE_DATA_PS_PLAYERS_BY_COUNTRY,
  info: 'dbi_info_alinea_ps_players_by_country',
  renderType: RenderType.COUNTRY_CHART,
  dataSource: 'alinea',
  metricValue: 'alinea',
  icon: 'alinea',
  titleTip: true,
  width: 360,
  chartMsg: {
    topDefault: 'bar',
    topDisableLine: true,
    topDisablePie: true,
    countryDefault: 'bar',
    countryDisableLine: true,
    countryDisablePie: true,
    statsDefault: 'bar',
  },
};

const DATE_TYPE_MAP: Record<string, string> = {
  day: 'daily',
  date: 'daily',
  daily: 'daily',
  week: 'weekly',
  weekly: 'weekly',
  month: 'monthly',
  monthly: 'monthly',
};

const normalizeConsoleDataDateType = (dateType = '') =>
  DATE_TYPE_MAP[dateType] || dateType;

export const isConsoleDataPlayersByCountryMetric = (metric = '') =>
  metric === CONSOLE_DATA_PS_PLAYERS_BY_COUNTRY;

export const getConsoleDataMetricsByDateType = (
  dateType = '',
  isCountries = false,
) => {
  const dateTypeCur = normalizeConsoleDataDateType(dateType);
  const baseList = pcConsoleDataList.filter((item) => {
    if (!Array.isArray(item?.dateEnum) || item.dateEnum.length < 1) {
      return true;
    }
    return item.dateEnum.includes(dateTypeCur);
  });

  if (!isCountries) {
    return baseList;
  }

  return [consoleDataPlayersByCountryMetric, ...baseList];
};

export const resolveConsoleDataMetric = (
  metric = '',
  dateType = '',
  isCountries = false,
) => {
  const metricsList = getConsoleDataMetricsByDateType(dateType, isCountries);
  if (metricsList.some((item) => item.value === metric)) {
    return metric;
  }
  if (isCountries && !metric) {
    return CONSOLE_DATA_COUNTRIES_DEFAULT_METRIC;
  }
  return metricsList[0]?.value || CONSOLE_DATA_DEFAULT_METRIC;
};

export const getConsoleDataMetricInfo = (
  metric = '',
  isCountries = false,
) => {
  if (isConsoleDataPlayersByCountryMetric(metric)) {
    return consoleDataPlayersByCountryMetric;
  }
  return pcConsoleDataList.find((item) => item.value === metric);
};
